import { calculateForumScore } from './forumScore';
import type { ForumBase, GrowthMetric } from '../types/forum';

export function growthPercent(current: number, previous: number | null | undefined): number | null {
  const prev = previous ?? 0;
  if (prev <= 0) return null;
  return ((current - prev) / prev) * 100;
}

export function getForumGrowthPercent(forum: ForumBase, metric: GrowthMetric): number | null {
  switch (metric) {
    case 'members':
      return growthPercent(forum.members_count, forum.previous_month_members_count);
    case 'posts':
      return growthPercent(forum.posts_count, forum.previous_month_posts_count);
    case 'topics':
      return growthPercent(forum.topics_count, forum.previous_month_topics_count);
    case 'score':
      return growthPercent(
        calculateForumScore(forum.topics_count, forum.posts_count, forum.members_count, forum.founded_year),
        calculateForumScore(
          forum.previous_month_topics_count ?? 0,
          forum.previous_month_posts_count ?? 0,
          forum.previous_month_members_count ?? 0,
          forum.founded_year
        )
      );
    default:
      return null;
  }
}

export function getRisingForums(
  forums: ForumBase[],
  metric: GrowthMetric = 'members',
  limit = 5
): Array<ForumBase & { growthPercent: number }> {
  return forums
    .map((f) => {
      const g = getForumGrowthPercent(f, metric);
      return g !== null && g > 0 ? { ...f, growthPercent: g } : null;
    })
    .filter((x): x is ForumBase & { growthPercent: number } => x !== null)
    .sort((a, b) => b.growthPercent - a.growthPercent)
    .slice(0, limit);
}

export function wasUpdatedWithinDays(
  forum: ForumBase,
  days: number
): boolean {
  const src = forum.updated_at || forum.created_at;
  if (!src) return false;
  const t = new Date(src).getTime();
  if (Number.isNaN(t)) return false;
  return Date.now() - t <= days * 24 * 60 * 60 * 1000;
}

export function forumSiteAgeYears(forum: ForumBase): number {
  const year = new Date().getFullYear();
  return Math.max(0, year - (forum.founded_year || year));
}
